import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  IconButton,
  Autocomplete,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import PhoneInput, { parsePhoneNumber } from "../../common/phone-input";
import { getCallingCodePhoneError } from "../../common/phone-validation";
import { useTenantCountryDefault } from "../../hooks/useTenantCountry";
import { Close } from "@mui/icons-material";
import ButtonOne from "../../common/normal-button";
import ButtonTwo from "../../common/dashboard-button";
import { useAuth } from "../../hooks/AuthProvider";
import { MFD_CLIENTS_ALL_ENDPOINT } from "../../config/urls";
import typography from "../../cssUtils/typography";
import { ColorsConstants } from "../../cssUtils/constants";

const LEAD_INTEREST_OPTIONS = [
  "Review My Investments",
  "Product Offerings",
  "One-Time Investment",
  "New SIP",
  "Financial Calculators",
  "Meeting / Conversation",
];

const REGISTERED_INTEREST_OPTIONS = [
  "New Investment",
  "Goal Planning",
  "New Product Offering",
];

const STATUS_OPTIONS = ["Open", "In Progress", "Converted", "Not Interested"];

const NAME_REGEX = /^[A-Za-z\s'.,-]+$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY_ERRORS = {
  client_id:    "",
  client_name:  "",
  phone_number: "",
  email:        "",
};

const LeadFormDialog = ({
  open,
  handleClose,
  onSave,
  editData,
  type,
  saving = false,
}) => {
  const isRegistered = type === "registered";
  const { getValidToken } = useAuth();

  const [form, setForm] = useState({
    client_name: "",
    country_code: "",
    phone_number: "",
    email: "",
    interest: "",
    status: "Open",
    comment: "",
    client_id: null,
  });

  const [errors, setErrors] = useState(EMPTY_ERRORS);
  const [clientOptions, setClientOptions] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);

  // Fetch all clients for the registered lead autocomplete
  useEffect(() => {
    if (!isRegistered || !open) return;
    setClientsLoading(true);
    (async () => {
      try {
        const token = await getValidToken();
        const tenant = localStorage.getItem("tenant");
        const res = await fetch(MFD_CLIENTS_ALL_ENDPOINT, {
          headers: { Authorization: `Bearer ${token}`, "X-Tenant-Id": tenant },
        });
        if (!res.ok) throw new Error("Failed to fetch clients");
        setClientOptions(await res.json());
      } catch (err) {
        console.error("Error fetching clients:", err);
      } finally {
        setClientsLoading(false);
      }
    })();
  }, [isRegistered, open, getValidToken]);

  // New records start on the tenant's country (Tenant Settings)
  useTenantCountryDefault(form.country_code, (v) => setForm((p) => ({ ...p, country_code: v })), {
    format: "+code",
    enabled: !editData,
  });

  // Initialise form when dialog opens or editData changes
  useEffect(() => {
    if (editData) {
      // New-format leads already carry a separate country_code from the API.
      // Older leads (created before the split) still have it embedded in the
      // phone number, so fall back to parsing it out of that combined value.
      let country_code = editData.country_code;
      let phone_number = editData.phone_number ?? editData.phone_no ?? "";
      if (!country_code) {
        const parsed = parsePhoneNumber(phone_number);
        country_code = parsed.countryCode;
        phone_number = parsed.phoneNo;
      }
      setForm({
        ...editData,
        client_id:    editData.client ?? editData.client_id ?? null,
        country_code,
        phone_number,
      });
    } else {
      setForm({
        client_name:  "",
        country_code: "",
        phone_number: "",
        email:        "",
        interest: isRegistered
          ? REGISTERED_INTEREST_OPTIONS[0]
          : LEAD_INTEREST_OPTIONS[0],
        status:   "Open",
        comment:  "",
        client_id: null,
      });
    }
    setErrors(EMPTY_ERRORS);
  }, [editData, open, isRegistered]);

  // Registered list endpoint returns `pan` but not the client FK id, so once
  // client options load, resolve the client_id from the lead's PAN.
  useEffect(() => {
    if (
      !isRegistered ||
      !open ||
      form.client_id != null ||
      !form.pan ||
      clientOptions.length === 0
    ) {
      return;
    }
    const match = clientOptions.find((c) => c.pan === form.pan);
    if (match) {
      setForm((prev) => ({ ...prev, client_id: match.id }));
    }
  }, [isRegistered, open, clientOptions, form.pan, form.client_id]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field in EMPTY_ERRORS) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const next = { ...EMPTY_ERRORS };
    if (isRegistered) {
      if (!form.client_id) next.client_id = "Client (PAN) is required";
    } else {
      const name = (form.client_name || "").trim();
      if (!name) next.client_name = "Name is required";
      else if (!NAME_REGEX.test(name)) next.client_name = "Please enter a valid name (letters only)";

      const phone = (form.phone_number || "").trim();
      if (!phone) next.phone_number = "Phone number is required";
      else next.phone_number = getCallingCodePhoneError(phone, form.country_code) || "";

      const email = (form.email || "").trim();
      if (email && !EMAIL_REGEX.test(email)) next.email = "Invalid email address";
    }
    setErrors(next);
    return Object.values(next).every((e) => !e);
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave(form);
  };

  const selectedClient =
    clientOptions.find((c) => c.id === form.client_id) ?? null;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {editData ? "Edit" : "Add"} {isRegistered ? "Registered Lead" : "Lead"}
        <IconButton onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          {/* Registered-only: client autocomplete (replaces PAN + client code) */}
          {isRegistered && (
            <Autocomplete
              options={clientOptions}
              loading={clientsLoading}
              value={selectedClient}
              onChange={(_, newValue) =>
                handleChange("client_id", newValue?.id ?? null)
              }
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              getOptionLabel={(opt) =>
                opt.name ? `${opt.pan} – ${opt.name}` : opt.pan || ""
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Client (PAN)"
                  required
                  error={!!errors.client_id}
                  helperText={errors.client_id}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {clientsLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Stack
                  {...props}
                  key={option.id}
                  direction="column"
                  gap={0}
                  sx={{
                    alignItems: "flex-start !important",
                    textAlign: "left",
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                    <Typography sx={{ ...typography.bodyLarge }}>
                      {option.pan}
                    </Typography>
                    <Typography
                      sx={{
                        ...typography.bodySmall,
                        color: ColorsConstants.primaryGray40,
                      }}
                    >
                      {option.source}
                    </Typography>
                  </Box>
                  {option.name && (
                    <Typography
                      sx={{
                        ...typography.bodySmall,
                        color: ColorsConstants.primaryGray40,
                      }}
                    >
                      {option.name}
                    </Typography>
                  )}
                </Stack>
              )}
            />
          )}

          {!isRegistered && (
            <>
              <TextField
                label="Name"
                value={form.client_name || ""}
                onChange={(e) => handleChange("client_name", e.target.value)}
                fullWidth
                required
                error={!!errors.client_name}
                helperText={errors.client_name}
              />
              <PhoneInput
                countryCode={form.country_code}
                phoneNo={form.phone_number || ""}
                onCountryCodeChange={(v) => handleChange("country_code", v)}
                onPhoneNoChange={(v) => handleChange("phone_number", v)}
                required
                fullWidth
                error={!!errors.phone_number}
                helperText={errors.phone_number}
              />
              <TextField
                label="Email"
                value={form.email || ""}
                onChange={(e) => handleChange("email", e.target.value)}
                fullWidth
                error={!!errors.email}
                helperText={errors.email}
              />
            </>
          )}
          <TextField
            select
            label="Interest"
            value={form.interest || ""}
            onChange={(e) => handleChange("interest", e.target.value)}
            fullWidth
          >
            {(isRegistered
              ? REGISTERED_INTEREST_OPTIONS
              : LEAD_INTEREST_OPTIONS
            ).map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Status"
            value={form.status || ""}
            onChange={(e) => handleChange("status", e.target.value)}
            fullWidth
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Comment"
            value={form.comment || ""}
            onChange={(e) => handleChange("comment", e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, px: 3 }}>
        <ButtonTwo onClick={handleClose} disabled={saving}>
          Cancel
        </ButtonTwo>
        <ButtonOne
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving…" : editData ? "Update" : "Add"}
        </ButtonOne>
      </DialogActions>
    </Dialog>
  );
};

export default LeadFormDialog;
